/*
  ЦИФРОВОЙ ДИАК ЗФ — BACKLOG COMMENTS
  Обсуждение внутри карточки канбана. Комментарии хранятся как поле
  BacklogItem.comments. Добавить может любой с правом редактирования;
  удалить — автор или админ/редактор.

  Формат тела комментария:
  - Новые комментарии хранятся как HTML (rich-text, TipTap).
  - Старые plain-text + markdown-картинки (`![alt](url)`) рендерятся
    через RichTextView с учётом эвристики `looksLikeHtml` — обратной
    совместимости ради.

  Картинки: можно вставить из буфера, перетащить или загрузить кнопкой.
  Файл загружается в `data/project-files/<projectId>` и регистрируется
  как обычное вложение карточки (чтобы не осиротел при «Перенести в
  другой проект»). В черновик HTML вставляем `<img src=...>`.
*/

import React, { useCallback, useRef, useState } from "react";
import {
  Check,
  Image as ImageIcon,
  Loader2,
  Pencil,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationsContext";
import {
  colorForAssignee,
  initialsFor,
  normalizeAssigneeKey,
  preferredAssigneeLabel,
} from "@/lib/assigneeDisplay";
import { cn } from "@/lib/utils";
import type { BacklogAttachment, BacklogComment } from "@/store/projectStore";
import { projectFileUrl, uploadBacklogAttachment } from "@/lib/api";
import { RichTextEditor, RichTextView, looksLikeHtml } from "./RichText";

interface BacklogCommentsProps {
  items: BacklogComment[];
  onChange: (next: BacklogComment[]) => void;
  /**
   * Опционально — id проекта, в который надо загружать картинки. Если не
   * передан, кнопка/вставка картинок не работают (комментарий читается, но
   * новые картинки добавить нельзя).
   */
  projectId?: string;
  /**
   * Колбэк добавления вложения к карточке. Когда пользователь вставляет в
   * комментарий картинку, файл уезжает в `data/project-files/<projectId>` и
   * параллельно регистрируется как вложение карточки — иначе при «Перенести
   * в другой проект» файл осиротеет.
   */
  onAttach?: (att: BacklogAttachment) => void;
  readOnly?: boolean;
}

const MAX_BODY = 100_000;

function newId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `c-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Человеческое «только что / 5 минут назад / 2 часа назад / вчера». */
function formatRelative(iso: string): string | null {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const min = Math.floor(ms / 60_000);
    if (min < 1) return "только что";
    if (min < 60) return `${min} мин назад`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} ч назад`;
    const d = Math.floor(hr / 24);
    if (d < 7) return `${d} д назад`;
    return null;
  } catch {
    return null;
  }
}

/**
 * Старые комментарии хранили картинки как markdown `![alt](url)`. Чтобы
 * RichTextView их корректно отрендерил, конвертируем в HTML на лету.
 * Если тело уже HTML — отдаём как есть.
 */
function commentBodyToHtml(body: string): string {
  if (!body) return "";
  if (looksLikeHtml(body)) return body;
  const escaped = body
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // Markdown-картинки (`![alt](url)`) — в <img>. Скобки/слэши при
  // экранировании HTML не трогаются, поэтому тот же regex работает.
  const withImages = escaped.replace(
    /!\[([^\]]*)\]\((\/api\/workspace\/project-files\/[^\s)]+)\)/g,
    (_full, alt: string, src: string) => {
      const safeAlt = String(alt).replace(/"/g, "&quot;");
      return `<img src="${src}" alt="${safeAlt}" />`;
    },
  );
  // Параграфы по двойному переводу строки, обычные \n внутри — <br/>.
  return withImages
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

/** Грубое «есть ли значимый текст» в HTML — нужно для disabled/empty-проверки. */
function isHtmlEmpty(html: string): boolean {
  if (!html) return true;
  // Нет тегов с медиа/контентом и нет видимого текста.
  const stripped = html
    .replace(/<img[^>]*>/gi, "x")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return stripped.length === 0;
}

export function BacklogComments({
  items,
  onChange,
  projectId,
  onAttach,
  readOnly = false,
}: BacklogCommentsProps) {
  const { user, canEdit } = useAuth();
  const { assigneeMatches } = useNotifications();
  const [draft, setDraft] = useState("");
  const [uploadingCount, setUploadingCount] = useState(0);
  /** Текущий редактируемый комментарий: id + черновик HTML. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const list = Array.isArray(items) ? items : [];

  const canPost = !readOnly && canEdit && !!user;
  const canUploadImages = canPost && !!projectId && !!onAttach;

  const myMatch =
    user ? assigneeMatches.get(normalizeAssigneeKey(user.login)) ?? null : null;
  const myLabel = user ? preferredAssigneeLabel(user.login, myMatch) : "";
  const myInitials = user
    ? initialsFor(myMatch?.displayName || myMatch?.login || user.login) || "?"
    : "?";
  const myBg = user
    ? colorForAssignee(normalizeAssigneeKey(myMatch?.login ?? user.login))
    : "#94a3b8";

  /** Аппендим <img src=...> к черновику HTML. */
  const appendImageHtml = useCallback((alt: string, url: string) => {
    setDraft((prev) => {
      const safeAlt = alt.replace(/"/g, "&quot;");
      const tag = `<p><img src="${url}" alt="${safeAlt}" /></p>`;
      const next = (prev || "") + tag;
      return next.slice(0, MAX_BODY);
    });
  }, []);

  const uploadAndInsert = useCallback(
    async (file: File) => {
      if (!projectId || !onAttach) return;
      if (!file.type.startsWith("image/")) {
        toast.error("В комментарий можно вставить только изображение");
        return;
      }
      setUploadingCount((n) => n + 1);
      try {
        const att = await uploadBacklogAttachment(projectId, file);
        onAttach(att);
        const url = projectFileUrl(projectId, att.id);
        appendImageHtml(att.name || "изображение", url);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Не удалось загрузить картинку";
        toast.error(msg);
      } finally {
        setUploadingCount((n) => Math.max(0, n - 1));
      }
    },
    [projectId, onAttach, appendImageHtml],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (!canUploadImages) return;
      const dt = e.clipboardData;
      if (!dt) return;
      const files: File[] = [];
      for (let i = 0; i < dt.items.length; i++) {
        const it = dt.items[i];
        if (!it) continue;
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f && f.type.startsWith("image/")) files.push(f);
        }
      }
      if (files.length === 0) return;
      e.preventDefault();
      for (const f of files) void uploadAndInsert(f);
    },
    [canUploadImages, uploadAndInsert],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!canUploadImages) return;
      const dt = e.dataTransfer;
      if (!dt || dt.files.length === 0) return;
      const images = Array.from(dt.files).filter((f) => f.type.startsWith("image/"));
      if (images.length === 0) return;
      e.preventDefault();
      for (const f of images) void uploadAndInsert(f);
    },
    [canUploadImages, uploadAndInsert],
  );

  const send = () => {
    const html = draft;
    if (isHtmlEmpty(html)) return;
    if (!user) {
      toast.error("Нужно войти, чтобы оставлять комментарии");
      return;
    }
    const c: BacklogComment = {
      id: newId(),
      authorLogin: user.login,
      authorId: user.id,
      body: html.slice(0, MAX_BODY),
      createdAt: new Date().toISOString(),
    };
    onChange([...list, c]);
    setDraft("");
  };

  const remove = (id: string) => {
    const c = list.find((x) => x.id === id);
    if (!c) return;
    const isAuthor = !!user && (c.authorId === user.id || c.authorLogin === user.login);
    const canRemove = isAuthor || !!user?.role && (user.role === "admin" || user.role === "editor");
    if (!canRemove) {
      toast.error("Удалить можно только свой комментарий");
      return;
    }
    if (!window.confirm("Удалить комментарий?")) return;
    onChange(list.filter((x) => x.id !== id));
  };

  /** Войти в режим редактирования. Текущее тело конвертируется в HTML, чтобы
   *  редактор работал в едином формате независимо от того, как был сохранён
   *  старый комментарий (plain-text / markdown / уже HTML). */
  const startEdit = (c: BacklogComment) => {
    setEditingId(c.id);
    setEditDraft(commentBodyToHtml(c.body));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft("");
  };

  const saveEdit = () => {
    if (!editingId) return;
    if (isHtmlEmpty(editDraft)) {
      toast.error("Комментарий не может быть пустым");
      return;
    }
    const next = list.map((c) =>
      c.id === editingId
        ? {
            ...c,
            body: editDraft.slice(0, MAX_BODY),
            editedAt: new Date().toISOString(),
          }
        : c,
    );
    onChange(next);
    setEditingId(null);
    setEditDraft("");
  };

  const draftEmpty = isHtmlEmpty(draft);

  return (
    <div className="space-y-3">
      {list.length === 0 ? (
        <p className="rounded-md border border-dashed border-border/70 bg-background/40 px-3 py-2.5 text-center text-[12px] italic text-muted-foreground">
          Пока обсуждения нет.{canPost ? " Станьте первым, кто оставит комментарий." : ""}
        </p>
      ) : (
        <ul className="space-y-2.5">
          {list.map((c) => {
            const match = assigneeMatches.get(normalizeAssigneeKey(c.authorLogin)) ?? null;
            const label = preferredAssigneeLabel(c.authorLogin, match);
            const initials = initialsFor(match?.displayName || match?.login || c.authorLogin) || "?";
            const bg = colorForAssignee(normalizeAssigneeKey(match?.login ?? c.authorLogin));
            const isAuthor = !!user && (c.authorId === user.id || c.authorLogin === user.login);
            const canRemove =
              isAuthor || (!!user && (user.role === "admin" || user.role === "editor"));
            // Редактировать может только автор (даже админ не правит чужой текст).
            const canEditOwn = isAuthor && !readOnly && canEdit;
            const isEditing = editingId === c.id;
            const relative = formatRelative(c.createdAt);
            const html = commentBodyToHtml(c.body);
            const editEmpty = isHtmlEmpty(editDraft);
            return (
              <li key={c.id} className="group/comment flex items-start gap-2.5">
                <span
                  className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white shadow-sm"
                  style={{ backgroundColor: bg }}
                  aria-hidden
                  title={label}
                >
                  {initials}
                </span>
                <div className="min-w-0 flex-1 rounded-lg border border-border/70 bg-background shadow-[0_1px_0_rgba(15,23,42,0.04)]">
                  <div className="flex items-center gap-2 border-b border-border/60 bg-muted/30 px-3 py-1.5 text-[11px]">
                    <span className="font-semibold text-foreground">{label}</span>
                    {isAuthor ? (
                      <span className="rounded-full border border-border/70 bg-background px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                        вы
                      </span>
                    ) : null}
                    <span
                      className="ml-auto text-muted-foreground"
                      title={formatWhen(c.createdAt)}
                    >
                      {relative ?? formatWhen(c.createdAt)}
                    </span>
                    {c.editedAt ? (
                      <span
                        className="italic text-muted-foreground/70"
                        title={`Изменено ${formatWhen(c.editedAt)}`}
                      >
                        (изм.)
                      </span>
                    ) : null}
                    {!isEditing && canEditOwn ? (
                      <button
                        type="button"
                        onClick={() => startEdit(c)}
                        className={cn(
                          "rounded p-1 text-muted-foreground opacity-0 transition-all group-hover/comment:opacity-100",
                          "hover:bg-muted hover:text-foreground",
                        )}
                        aria-label="Редактировать комментарий"
                        title="Редактировать"
                      >
                        <Pencil className="h-3 w-3" strokeWidth={2} />
                      </button>
                    ) : null}
                    {!isEditing && !readOnly && canRemove ? (
                      <button
                        type="button"
                        onClick={() => remove(c.id)}
                        className={cn(
                          "rounded p-1 text-muted-foreground opacity-0 transition-all group-hover/comment:opacity-100",
                          "hover:bg-destructive/10 hover:text-destructive",
                        )}
                        aria-label="Удалить комментарий"
                        title="Удалить"
                      >
                        <Trash2 className="h-3 w-3" strokeWidth={2} />
                      </button>
                    ) : null}
                  </div>
                  {isEditing ? (
                    <div className="space-y-2 p-2">
                      <RichTextEditor
                        value={editDraft}
                        onChange={(h) =>
                          setEditDraft(h.length > MAX_BODY ? h.slice(0, MAX_BODY) : h)
                        }
                        placeholder="Комментарий…"
                        minHeight="5rem"
                        autoFocus
                        onSubmit={saveEdit}
                      />
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
                        >
                          <X className="h-3.5 w-3.5" strokeWidth={2} />
                          Отмена
                        </button>
                        <button
                          type="button"
                          onClick={saveEdit}
                          disabled={editEmpty}
                          className={cn(
                            "inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1 text-[11.5px] font-semibold shadow-sm transition-colors",
                            !editEmpty
                              ? "bg-[var(--nn-blue)] text-white hover:opacity-90"
                              : "cursor-not-allowed bg-muted text-muted-foreground",
                          )}
                          title="Сохранить (Cmd/Ctrl + Enter)"
                        >
                          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                          Сохранить
                        </button>
                      </div>
                    </div>
                  ) : (
                    <RichTextView
                      html={html}
                      className="px-3 py-2 text-[13px] leading-relaxed text-foreground/90 [&_img]:my-1.5 [&_img]:max-h-[320px] [&_img]:rounded [&_img]:border [&_img]:border-border [&_img]:shadow-sm"
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {canPost ? (
        <div className="flex items-start gap-2.5">
          <span
            className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white shadow-sm"
            style={{ backgroundColor: myBg }}
            aria-hidden
            title={myLabel}
          >
            {myInitials}
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <RichTextEditor
              value={draft}
              onChange={(html) =>
                setDraft(html.length > MAX_BODY ? html.slice(0, MAX_BODY) : html)
              }
              placeholder={
                canUploadImages
                  ? "Напишите комментарий — можно вставить изображение из буфера или перетащить файл…"
                  : "Напишите комментарий, задайте вопрос или упомяните детали…"
              }
              minHeight="6.5rem"
              onSubmit={send}
              onPasteCapture={handlePaste}
              onDropCapture={handleDrop}
            />
            <div className="flex items-center justify-between gap-2 px-1">
              <span className="text-[10.5px] text-muted-foreground/80">
                <kbd className="rounded border border-border/70 bg-background px-1 py-[1px] font-mono text-[10px] text-foreground/80">
                  {typeof navigator !== "undefined" && /Mac/i.test(navigator.platform) ? "⌘" : "Ctrl"}
                </kbd>
                {" + "}
                <kbd className="rounded border border-border/70 bg-background px-1 py-[1px] font-mono text-[10px] text-foreground/80">
                  Enter
                </kbd>
                {" — отправить"}
              </span>
              <div className="flex items-center gap-1.5">
                {uploadingCount > 0 ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Загружаю…
                  </span>
                ) : null}
                {canUploadImages ? (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      onChange={(e) => {
                        const files = Array.from(e.target.files ?? []);
                        e.target.value = "";
                        for (const f of files) void uploadAndInsert(f);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
                      title="Прикрепить изображение"
                    >
                      <ImageIcon className="h-3.5 w-3.5" strokeWidth={2} />
                      Картинка
                    </button>
                  </>
                ) : null}
                {!draftEmpty ? (
                  <button
                    type="button"
                    onClick={() => setDraft("")}
                    className="rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    Очистить
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={send}
                  disabled={draftEmpty}
                  className={cn(
                    "inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1 text-[12px] font-semibold shadow-sm transition-colors",
                    !draftEmpty
                      ? "bg-[var(--nn-blue)] text-white hover:opacity-90"
                      : "cursor-not-allowed bg-muted text-muted-foreground",
                  )}
                  title="Отправить (Cmd/Ctrl + Enter)"
                >
                  <Send className="h-3.5 w-3.5" strokeWidth={2} />
                  Отправить
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
